import { Enemy, SimConstants } from "@highmarch/domain";
import type { EnemyView } from "./enemyView";

/**
 * A blocked raider's blow on the soldier holding it, timed to the sim's strike (in-game check 2026-10-22). The sim
 * strikes on the tick it emits EnemyBlocked, and the soldier flinches that tick (troops.ts); the raider's `attack`
 * started on the same event, so its weapon came down 0.2–0.55 s after the flinch. As the troops' own swings
 * (troops.ts TROOP_CLIP): the first blow of a contact enters the clip at its impact frame, and while the raider stays
 * held its next swing starts ahead of the strike from its cadence (`Enemy.attackCooldown`), so the weapon lands on the
 * strike tick. Played `solo`: the units v2 clips key every part the idle keys too, so averaged with the idle under them
 * the swing played at half its reach (bossClips.ts made the bosses' solo for the same reason).
 */
export const ENEMY_STRIKE = {
  /** Where the blow lands in an `attack` clip (art/hq/README.md: "fire the sim's hit at ~60 % of the clip"). */
  impactFraction: 0.6,
  /**
   * Measured strikes (s; the weapon's swing coming to rest) that sit far from 60 %: the runner's quick jab, the
   * stunner's late overhead, and every boss_* (bosses v2 all land at 0.35 s of 0.93).
   */
  impactAt: { enemy_runner: 0.18, enemy_stunner: 0.62 } as Readonly<Record<string, number>>,
  bossImpact: 0.35,
  /** A raider counts as still held this many strike intervals after its last blow (then its next swing is anticipated). */
  engagedIntervals: 1.5,
} as const;

/** The model name without the `hq:` prefix and the LOD suffix. */
const baseModel = (v: EnemyView): string => v.model.replace(/^hq:/, "").replace(/_lod1$/, "");

/** Seconds into its `attack` clip at which `v`'s blow lands (clip length `len`). */
export function impactOf(v: EnemyView, len: number): number {
  const m = baseModel(v);
  const at = ENEMY_STRIKE.impactAt[m] ?? (m.startsWith("boss_") ? ENEMY_STRIKE.bossImpact : ENEMY_STRIKE.impactFraction * len);
  return Math.max(0, Math.min(len, at));
}

/** Ticks of cooldown left (as read after a tick) at which a swing must start for its impact to land on the strike. */
export function leadTicks(impact: number): number {
  return Math.max(0, Math.floor(impact * SimConstants.TicksPerSecond) - 1);
}

/** Per view: the tick of its last blow and of the swing it started ahead of one (-1: none). */
export interface StrikeState { lastBlow: number; swungAt: number }

/** One view's strike timing (EnemyViews keeps one per live view). */
export class EnemyStrike {
  private readonly state = new Map<number, StrikeState>();

  /**
   * The sim struck with enemy `id` this tick (EnemyBlocked): unless a swing started ahead of it is already on its way
   * down, the clip enters at its impact frame so the blow shows now. `play(startAt)` plays `attack` and returns its length.
   */
  struck(id: number, v: EnemyView, tick: number, play: (startAt: number) => number): void {
    const s = this.of(id);
    const anticipated = s.swungAt >= 0 && tick - s.swungAt <= leadTicks(impactOf(v, this.lenOf(v))) + 2;
    s.lastBlow = tick;
    if (anticipated || v.rig.busy) return;
    play(impactOf(v, this.lenOf(v)));
  }

  /**
   * After each tick, for a raider still held (struck within the last `engagedIntervals` strikes, not moving): starts its
   * next swing so that it lands on the strike. `interval` is the strike cadence in ticks.
   */
  anticipate(id: number, e: Enemy, v: EnemyView, tick: number, interval: number, play: (startAt: number) => number): void {
    const s = this.state.get(id);
    if (!s || s.lastBlow < 0 || tick - s.lastBlow > interval * ENEMY_STRIKE.engagedIntervals) return;
    if (v.rig.busy || v.dead > 0 || v.doomed || !v.cur.equals(v.prev)) return;
    const impact = impactOf(v, this.lenOf(v)), lead = leadTicks(impact);
    if (e.attackCooldown !== lead) return;
    play(Math.max(0, impact - (lead + 1) / SimConstants.TicksPerSecond));
    s.swungAt = tick;
  }

  forget(id: number): void { this.state.delete(id); }
  clear(): void { this.state.clear(); }

  private of(id: number): StrikeState {
    let s = this.state.get(id);
    if (!s) { s = { lastBlow: -1, swungAt: -1 }; this.state.set(id, s); }
    return s;
  }

  /** The length of `v`'s attack clip (s), 0 when it has none. */
  private lenOf(v: EnemyView): number { return v.rig.clipLength("attack"); }
}
